import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  getFollowers,
  getFollowing,
  followUser,
  unfollowUser,
} from "../../Api/followService";
import { useAuth } from "../../AuthContext";

const Followers = () => {
  const { t, i18n } = useTranslation();
  const [followers, setFollowers] = useState([]);
  const [following, setFollowing] = useState([]);
  const [loading, setLoading] = useState(false);
  const [followerSearch, setFollowerSearch] = useState("");
  const [followingSearch, setFollowingSearch] = useState("");
  const [activeTab, setActiveTab] = useState("followers");
  const { userId } = useAuth();
  const languageDirection = i18n.language === "ar" ? "rtl" : "ltr";

  const handleFollowToggle = async (targetUserId) => {
    const isAlreadyFollowing = following.some(
      (user) => user.id === targetUserId
    );
    try {
      if (isAlreadyFollowing) {
        const success = await unfollowUser(targetUserId);
        if (success) {
          setFollowing(following.filter((user) => user.id !== targetUserId));
          setFollowers(
            followers.map((user) =>
              user.id === targetUserId ? { ...user, isFollowing: false } : user
            )
          );
        }
      } else {
        const success = await followUser(targetUserId);
        if (success) {
          const updatedFollowing = await getFollowing(userId);
          setFollowing(updatedFollowing);
          setFollowers(
            followers.map((user) =>
              user.id === targetUserId ? { ...user, isFollowing: true } : user
            )
          );
        }
      }
    } catch (error) {
      console.error("Error toggling follow:", error);
    }
  };

  const filteredFollowers = followers.filter((follower) =>
    follower.arabicFullName
      ?.toLowerCase()
      .includes(followerSearch.toLowerCase())
  );

  const filteredFollowing = following.filter((followed) =>
    followed.arabicFullName
      ?.toLowerCase()
      .includes(followingSearch.toLowerCase())
  );

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const followersResponse = await getFollowers(userId);
        const followingResponse = await getFollowing(userId);

        console.log("Followers:", followersResponse);
        console.log("Following:", followingResponse);

        setFollowers(
          Array.isArray(followersResponse.items) ? followersResponse.items : []
        );
        setFollowing(
          Array.isArray(followingResponse.items) ? followingResponse.items : []
        );
      } catch (error) {
        console.error("Error fetching followers or following:", error);
      }
      setLoading(false);
    };

    fetchData();
  }, [userId]);

  return (
    <main>
      <div className="container py-5" dir={languageDirection}>
        <div className="row justify-content-center">
          <div className="col-lg-10">
            <div className="card shadow rounded-4 border-0 p-4">
              <div className="card-body">
                <h1 className="h3 mb-4 text-center fw-bold text-primary-custom">
                  {t("followers_following")}
                </h1>

                {/* Tabs */}
                <ul className="nav nav-tabs nav-fill mb-4">
                  <li className="nav-item">
                    <button
                      className={`nav-link ${
                        activeTab === "followers" ? "active" : ""
                      }`}
                      onClick={() => setActiveTab("followers")}
                    >
                      {t("followers")}
                    </button>
                  </li>
                  <li className="nav-item">
                    <button
                      className={`nav-link ${
                        activeTab === "following" ? "active" : ""
                      }`}
                      onClick={() => setActiveTab("following")}
                    >
                      {t("following")}
                    </button>
                  </li>
                </ul>

                {/* Followers Tab */}
                {activeTab === "followers" && (
                  <>
                    {/* Search */}
                    <div className="input-group mb-4">
                      <span className="input-group-text">
                        <i className="fas fa-search"></i>
                      </span>
                      <input
                        type="text"
                        className="form-control"
                        placeholder={t("search_followers")}
                        value={followerSearch}
                        onChange={(e) => setFollowerSearch(e.target.value)}
                      />
                    </div>

                    {/* Followers List */}
                    <div id="followersList">
                      {filteredFollowers.length > 0 ? (
                        filteredFollowers.map((follower) => (
                          <div
                            key={follower.id}
                            className="card user-card mb-3 border-0 shadow-sm"
                          >
                            <div className="card-body d-flex align-items-center justify-content-between">
                              <div className="d-flex align-items-center">
                                <a
                                  href={`/speaker-detail/${follower.id}`}
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    textDecoration: "none",
                                  }}
                                >
                                  <img
                                    src={
                                      follower.profileImage ||
                                      "assets/speakers/default-avatar.jpg"
                                    }
                                    className="user-avatar me-3"
                                    alt={
                                      i18n.language === "ar"
                                        ? follower.arabicFullName
                                        : follower.englishFullName
                                    }
                                    style={{
                                      width: "60px",
                                      height: "60px",
                                      objectFit: "cover",
                                      borderRadius: "50%",
                                      marginRight: "10px", 
                                    }}
                                  />
                                  <div>
                                    <h5 className="mb-1">
                                      {i18n.language === "ar"
                                        ? follower.arabicFullName
                                        : follower.englishFullName}
                                    </h5>
                                    <p className="text-muted mb-0">
                                      {(i18n.language === "ar"
                                        ? follower.arabicShortBiography
                                        : follower.englishShortBiography
                                      )?.slice(0, 50) +
                                        ((i18n.language === "ar"
                                          ? follower.arabicShortBiography
                                          : follower.englishShortBiography
                                        )?.length > 50
                                          ? "..."
                                          : "")}
                                    </p>
                                  </div>
                                </a>
                              </div>
                              <button
                                className={`btn ${
                                  follower.isFollowing
                                    ? "btn-light"
                                    : "btn-primary-custom"
                                } btn-follow`}
                                onClick={() => handleFollowToggle(follower.id)}
                              >
                                <i
                                  className={`fas ${
                                    follower.isFollowing
                                      ? "fa-check"
                                      : "fa-plus"
                                  } me-1`}
                                ></i>
                                {follower.isFollowing
                                  ? t("following")
                                  : t("follow")}
                              </button>
                            </div>
                          </div>
                        ))
                      ) : (
                        <p className="text-center text-muted">
                          {t("no_followers_found")}
                        </p>
                      )}
                    </div>
                  </>
                )}

                {/* Following Tab */}
                {activeTab === "following" && (
                  <>
                    {/* Search */}
                    <div className="input-group mb-4">
                      <span className="input-group-text">
                        <i className="fas fa-search"></i>
                      </span>
                      <input
                        type="text"
                        className="form-control"
                        placeholder={t("search_following")}
                        value={followingSearch}
                        onChange={(e) => setFollowingSearch(e.target.value)}
                      />
                    </div>

                    {/* Following List */}
                    <div id="followingList">
                      {filteredFollowing.length > 0 ? (
                        filteredFollowing.map((followed) => (
                          <div
                            key={followed.id}
                            className="card user-card mb-3 border-0 shadow-sm"
                          >
                            <div className="card-body d-flex align-items-center justify-content-between">
                              <div className="d-flex align-items-center">
                                <a
                                  href={`/speaker-detail/${followed.id}`}
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    textDecoration: "none",
                                  }}
                                >
                                  <img
                                    src={
                                      followed.profileImage ||
                                      "assets/speakers/default-avatar.jpg"
                                    }
                                    className="user-avatar me-3"
                                    alt={
                                      i18n.language === "ar"
                                        ? followed.arabicFullName
                                        : followed.englishFullName
                                    }
                                    style={{
                                      width: "60px",
                                      height: "60px",
                                      objectFit: "cover",
                                      borderRadius: "50%",
                                      marginRight: "10px",
                                    }}
                                  />
                                  <div>
                                    <h5 className="mb-1">
                                      {i18n.language === "ar"
                                        ? followed.arabicFullName
                                        : followed.englishFullName}
                                    </h5>
                                    <p className="text-muted mb-0">
                                      {(i18n.language === "ar"
                                        ? followed.arabicShortBiography
                                        : followed.englishShortBiography
                                      )?.slice(0, 70) +
                                        ((i18n.language === "ar"
                                          ? followed.arabicShortBiography
                                          : followed.englishShortBiography
                                        )?.length > 70
                                          ? "..."
                                          : "")}
                                    </p>
                                  </div>
                                </a>
                              </div>
                              <button
                                className="btn btn-light btn-unfollow"
                                onClick={() => handleFollowToggle(followed.id)}
                              >
                                <i className="fas fa-user-times me-1"></i>
                                {t("unfollow")}
                              </button>
                            </div>
                          </div>
                        ))
                      ) : (
                        <p className="text-center text-muted">
                          {t("no_following_found")}
                        </p>
                      )}
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </main>
  );
};

export default Followers;
